import React, { useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import { AppContext } from '../App';
import { useAuth } from '../context/AuthContext';
import { getMentorAppStatus, canReapply, reapplyUnlockDate } from '../utils/mentorApp';
import { BecomeMentorModal } from './dashboard/MenteeDashboard';
import './BecomeMentor.css';

const benefits = [
  { icon: '💰', title: 'Keep 85% of Every Session', desc: 'Set your own rates. Top mentors earn ₹40,000–₹1,60,000/month. Weekly payouts to your bank account.' },
  { icon: '🗓️', title: 'Work on Your Schedule', desc: 'Set your available hours. Accept only bookings you want. No minimum hours required.' },
  { icon: '🌍', title: 'Reach Global Learners', desc: 'Connect with motivated learners from 50+ countries who are serious about growing.' },
  { icon: '📈', title: 'Build Your Brand', desc: 'A verified DevBrains profile boosts your credibility and creates inbound opportunities.' },
];

const requirements = [
  '3+ years of professional tech experience',
  'A LinkedIn profile showing your work history',
  'Availability for at least 4 hours/week',
  'Proficient English communication skills',
  'Passion for teaching and mentoring others',
];

export default function BecomeMentor() {
  const { setModalOpen } = useContext(AppContext);
  const { user } = useAuth();
  const [showApplyModal, setShowApplyModal] = useState(false);

  // Already a mentor — show a friendly redirect panel instead of the full apply page
  if (user?.role === 'mentor') {
    return (
      <div style={{ minHeight:'60vh', display:'flex', alignItems:'center', justifyContent:'center', padding:32 }}>
        <div style={{ maxWidth:520, textAlign:'center' }}>
          <div style={{ fontSize:56, marginBottom:16 }}>🎓</div>
          <h2 style={{ fontSize:'1.75rem', fontWeight:900, color:'#111827', marginBottom:12 }}>You're already a DevBrains Mentor!</h2>
          <p style={{ color:'#6b7280', lineHeight:1.7, marginBottom:28 }}>
            Hi <strong>{user.name.split(' ')[0]}</strong>! You're already part of our mentor community.
            Head to your dashboard to manage sessions, update your profile, and track earnings.
          </p>
          <div style={{ display:'flex', gap:12, justifyContent:'center', flexWrap:'wrap' }}>
            <Link to="/dashboard/mentor" className="btn btn-primary" style={{ padding:'12px 24px', fontWeight:800 }}>📊 Go to Dashboard →</Link>
            <Link to="/dashboard/mentor/availability" className="btn btn-outline" style={{ padding:'12px 24px' }}>📅 Manage Availability</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="become-mentor-page">
      {/* Hero */}
      <div className="mentor-hero">
        <div className="mentor-hero-inner">
          <div className="mentor-hero-left">
            <div className="section-label">For Mentors</div>
            <h1 className="t-h2" style={{fontSize:'clamp(2rem,4vw,3rem)',marginTop:12,marginBottom:16}}>Share Your Expertise.<br/>Earn What You're Worth.</h1>
            <p style={{fontSize:'1.0625rem',color:'var(--ink2)',lineHeight:1.75,marginBottom:28}}>
              Join 2,400+ verified mentors helping the next generation of tech professionals. Set your rates, pick your hours, and make a real impact.
            </p>
            <ul className="req-list">
              {requirements.map(r => (
                <li key={r}><span className="req-check">✅</span>{r}</li>
              ))}
            </ul>
            {/* Earnings card */}
            <div className="earn-card">
              <div className="earn-row">
                <div className="earn-item"><div className="earn-num">85%</div><div className="earn-lbl">Revenue Share</div></div>
                <div className="earn-item"><div className="earn-num">₹40K+</div><div className="earn-lbl">Monthly Avg Earn</div></div>
                <div className="earn-item"><div className="earn-num">Weekly</div><div className="earn-lbl">Payouts</div></div>
              </div>
            </div>
          </div>

          {/* Apply CTA card — shows status if already applied */}
          {(() => {
            const app = user ? getMentorAppStatus(user.id) : null;
            if (app?.status === 'pending') {
              return (
                <div className="reg-card" style={{display:'flex',flexDirection:'column',alignItems:'center',textAlign:'center',gap:20,padding:'40px 32px'}}>
                  <div style={{fontSize:52}}>⏳</div>
                  <div>
                    <h3 style={{fontWeight:900,fontSize:'1.3rem',color:'#92400e',marginBottom:8}}>Application Under Review</h3>
                    <p style={{fontSize:'.88rem',color:'#b45309',lineHeight:1.75,marginBottom:0}}>
                      Your mentor application is being reviewed by our team. You will hear back within 24–48 hours.
                    </p>
                    {app.submittedAt && (
                      <p style={{fontSize:'.78rem',color:'#d97706',marginTop:10,fontWeight:700}}>
                        Submitted on {new Date(app.submittedAt).toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'})}
                      </p>
                    )}
                  </div>
                  <span style={{background:'#fef3c7',border:'1.5px solid #fde68a',color:'#92400e',padding:'8px 24px',borderRadius:999,fontWeight:800,fontSize:'.8rem',letterSpacing:'.04em'}}>PENDING REVIEW</span>
                </div>
              );
            }
            if (app?.status === 'rejected' && !canReapply(app)) {
              return (
                <div className="reg-card" style={{display:'flex',flexDirection:'column',alignItems:'center',textAlign:'center',gap:20,padding:'40px 32px'}}>
                  <div style={{fontSize:52}}>❌</div>
                  <div>
                    <h3 style={{fontWeight:900,fontSize:'1.3rem',color:'#991b1b',marginBottom:8}}>Application Not Approved</h3>
                    <p style={{fontSize:'.88rem',color:'#b91c1c',lineHeight:1.75,marginBottom:0}}>
                      Your application was not approved at this time. You can reapply on <strong>{reapplyUnlockDate(app)}</strong>.
                    </p>
                  </div>
                  <span style={{background:'#fee2e2',border:'1.5px solid #fecaca',color:'#991b1b',padding:'8px 24px',borderRadius:999,fontWeight:800,fontSize:'.8rem',letterSpacing:'.04em'}}>NOT APPROVED</span>
                </div>
              );
            }
            return (
              <div className="reg-card" style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',textAlign:'center',gap:24,padding:'40px 32px'}}>
                <div style={{fontSize:56}}>🚀</div>
                <div>
                  <h3 style={{fontWeight:900,fontSize:'1.4rem',color:'#111827',marginBottom:10}}>Register Quickly and Apply as a Mentor</h3>
                  <p style={{fontSize:'.9rem',color:'#6b7280',lineHeight:1.75,marginBottom:0}}>
                    Takes less than 3 minutes. Set your own rates and start earning within 30 minutes of approval.
                  </p>
                </div>
                <div style={{display:'flex',flexDirection:'column',gap:10,width:'100%',maxWidth:320}}>
                  <button
                    className="btn btn-primary"
                    style={{width:'100%',justifyContent:'center',padding:'15px',borderRadius:'12px',fontSize:'1rem',fontWeight:800}}
                    onClick={() => user ? setShowApplyModal(true) : setModalOpen(true)}
                  >
                    {app?.status === 'rejected' ? 'Reapply as a Mentor →' : 'Apply as a Mentor →'}
                  </button>
                </div>
                <p style={{fontSize:'.75rem',color:'#9ca3af',marginTop:-8}}>
                  No commitment required · We'll respond within 48 hours
                </p>
              </div>
            );
          })()}
        </div>
      </div>

      {/* Benefits */}
      <div style={{background:'#fff',borderBottom:'1px solid var(--line)'}}>
        <div className="section-wrap-inner">
          <div style={{textAlign:'center',marginBottom:48}}>
            <div className="section-label">Why Mentors Love DevBrains</div>
            <h2 className="t-h2">Built for Mentors, Not Just Mentees</h2>
          </div>
          <div className="benefits-grid">
            {benefits.map(b => (
              <div key={b.title} className="benefit-card">
                <div className="benefit-ico">{b.icon}</div>
                <h5>{b.title}</h5>
                <p>{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {showApplyModal && (
        <BecomeMentorModal user={user} onClose={() => setShowApplyModal(false)} />
      )}
    </div>
  );
}
